// 2026-08 — First-login push opt-in banner.
// Chrome/Android: "Enable" asks for permission via user gesture, then subscribes.
// iPhone Safari (not installed): web push only works from an installed PWA,
// so we point the user at the install guide instead of silently failing.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Smartphone } from 'lucide-react';
import { usePushNotifications, isIOS, isStandalone, pushSupported } from '../lib/usePushNotifications';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';

const DISMISS_KEY = 'stellar.push.banner.dismissed';

export const PushPrompt = () => {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const { subscribe } = usePushNotifications({ userId: user?.id, workspaceId: currentWorkspace?.id, autoEnroll: false });
  const dismissKey = user?.id ? `${DISMISS_KEY}:${user.id}` : DISMISS_KEY;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === '1'; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);

  if (!user || dismissed) return null;
  const iosNeedsInstall = isIOS() && !isStandalone();
  if (!iosNeedsInstall) {
    if (!pushSupported()) return null;
    if (Notification.permission !== 'default') return null;
  }

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(dismissKey, '1'); } catch { /* noop */ }
  };

  const enable = async () => {
    setBusy(true);
    const ok = await subscribe();
    setBusy(false);
    if (ok) {
      toast.success('Notifications enabled — you\'ll now get task updates on this device.');
      dismiss();
    } else if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
      toast.error('Notifications are blocked in your browser settings for this site.');
      dismiss();
    }
  };

  return (
    <div className="mx-3 mt-2 md:mx-4 rounded-xl border border-gold/25 bg-gold/[0.06] px-3 py-2.5 flex items-center gap-3" data-testid="push-prompt-banner">
      {iosNeedsInstall ? <Smartphone className="h-4 w-4 text-gold shrink-0" /> : <Bell className="h-4 w-4 text-gold shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-semibold text-foreground leading-tight">
          {iosNeedsInstall ? 'Install the app to get notifications' : 'Turn on notifications'}
        </p>
        <p className="text-[11px] text-muted-foreground/70 leading-snug">
          {iosNeedsInstall
            ? 'On iPhone, add Stellar CRM to your Home Screen first — then notifications can be enabled.'
            : 'So you never miss a task update, review, or comment.'}
        </p>
      </div>
      {iosNeedsInstall ? (
        <button
          onClick={() => { navigate('/tasks/install-guide'); }}
          className="text-[11px] font-semibold text-gold bg-gold/15 hover:bg-gold/25 rounded-lg px-3 py-1.5 shrink-0 transition-colors"
          data-testid="push-prompt-install-btn"
        >
          How to install
        </button>
      ) : (
        <button
          onClick={enable}
          disabled={busy}
          className="text-[11px] font-semibold text-gold bg-gold/15 hover:bg-gold/25 rounded-lg px-3 py-1.5 shrink-0 transition-colors disabled:opacity-50"
          data-testid="push-prompt-enable-btn"
        >
          {busy ? 'Enabling…' : 'Enable'}
        </button>
      )}
      <button onClick={dismiss} className="text-muted-foreground/40 hover:text-muted-foreground/70 shrink-0" aria-label="Dismiss" data-testid="push-prompt-dismiss">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default PushPrompt;
